import { io, Socket } from 'socket.io-client';
import { WEBSOCKET_CONFIG, WS_EVENTS } from '@/lib/constants';

export interface WebSocketOptions {
  url: string;
  path: string;
  authId: string; // Base64 encoded username:password
  agentName: string;
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  onError?: (error: Error) => void;
  onReconnect?: (attemptNumber: number) => void;
}

export function createWebSocketConnection(options: WebSocketOptions): Socket {
  const {
    url,
    path,
    authId,
    agentName,
    onConnect,
    onDisconnect,
    onError,
    onReconnect,
  } = options;

  // Create socket connection
  const socket = io(url, {
    path,
    transports: ['websocket', 'polling'],
    auth: {
      token: `Basic ${authId}`,
    },
    extraHeaders: {
      authorization: `Basic ${authId}`,
    },
    query: {
      auth: `Basic ${authId}`,
    },
    reconnection: true,
    reconnectionDelay: WEBSOCKET_CONFIG.reconnectionDelay,
    reconnectionAttempts: WEBSOCKET_CONFIG.reconnectionAttempts,
    autoConnect: false, // Manual connect to have more control
  });

  // Built-in Socket.IO events
  socket.on(WS_EVENTS.CONNECT, () => {
    console.log('✅ WebSocket connected');

    // Emit user.connect event after connection
    socket.emit(WS_EVENTS.USER_CONNECT, {
      agentName,
      timestamp: Date.now(),
      sessionId: crypto.randomUUID(),
    });

    onConnect?.();
  });

  socket.on(WS_EVENTS.DISCONNECT, (reason) => {
    console.log('❌ WebSocket disconnected:', reason);
    onDisconnect?.(reason);
  });

  socket.on(WS_EVENTS.CONNECT_ERROR, (error) => {
    console.error('❌ WebSocket connection error:', error);
    onError?.(error as Error);
  });

  socket.on(WS_EVENTS.RECONNECT, (attemptNumber) => {
    console.log(`🔄 WebSocket reconnected after ${attemptNumber} attempts`);
    onReconnect?.(attemptNumber);
  });

  socket.on(WS_EVENTS.RECONNECT_ATTEMPT, (attemptNumber) => {
    console.log(`🔄 WebSocket reconnection attempt ${attemptNumber}...`);
  });

  socket.on(WS_EVENTS.RECONNECT_ERROR, (error) => {
    console.error('❌ WebSocket reconnection error:', error);
  });

  socket.on(WS_EVENTS.RECONNECT_FAILED, () => {
    console.error('❌ WebSocket reconnection failed after max attempts');
  });

  return socket;
}

export function disconnectWebSocket(socket: Socket, agentName: string): void {
  if (socket.connected) {
    // Emit user.disconnect event before closing
    socket.emit(WS_EVENTS.USER_DISCONNECT, {
      agentName,
      timestamp: Date.now(),
      reason: 'user_action',
    });

    socket.disconnect();
  }
}
